import React from 'react'
import { css, cx } from '@emotion/css'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { FormattedMessage } from 'react-intl'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { AttackStatus, AttackType } from '../attack-status'
import { CsUserIcon } from '../../legacy-design-system/icons'
import { WithTooltip, tooltipTitleCss } from '../../design-system'
import { metadataCss } from '../../legacy-design-system/text'
import { PopperRefHandler } from '../../legacy-design-system/popper'

const transitionName = 'ContrastAttacker_transition'
const transitionDuration = 500

export interface Attacker {
  readonly name: string
  readonly status: AttackStatus
  readonly attackTypes: AttackType[]
  readonly totalAttacks: number
  readonly sourceIPs: string[]
  readonly sourceName?: string
  readonly unknown: boolean
  readonly disabled: boolean
  readonly onClick: (clickProps: { readonly shiftKey: boolean }) => void
}

export interface RemainingAttackers {
  readonly attackerCount: number
  readonly attackCount: number
  readonly disabled: boolean
  readonly onClick: (clickProps: { readonly shiftKey: boolean }) => void
}

export interface AttackersProps {
  readonly attackers: Attacker[]
  readonly remainingAttackers?: RemainingAttackers
}

export function Attackers({ attackers, remainingAttackers }: AttackersProps) {
  return (
    <div
      className={css`
        min-height: 350px;
        padding: 10px 30px;

        @media (min-width: 992px) {
          border-right: 1px solid ${theme.color.neutral30};
          margin-right: -1px;
        }
      `}
    >
      <TransitionGroup>
        {(attackers.length || remainingAttackers) && (
          <CSSTransition
            classNames={transitionName}
            timeout={transitionDuration}
          >
            <React.Fragment>
              {attackers.map((attacker, index) => {
                const { name } = attacker
                return (
                  <Attacker
                    {...attacker}
                    last={!remainingAttackers && index === attackers.length - 1}
                    key={name}
                  />
                )
              })}
              {remainingAttackers && (
                <RemainingAttackers
                  {...remainingAttackers}
                  key="remaining_attackers"
                />
              )}
            </React.Fragment>
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  )
}

export function Attacker({
  name,
  attackTypes,
  status,
  totalAttacks,
  unknown,
  sourceName,
  onClick,
  disabled,
  sourceIPs,
  last
}: Attacker & { readonly last: boolean }) {
  return (
    <AttackerContainer
      disabled={disabled}
      onClick={(e) => onClick({ shiftKey: e.shiftKey })}
      last={last}
    >
      <UserIcon known={!!sourceName} />
      <div
        className={css`
          padding-left: 8px;
          flex-grow: 1;
        `}
      >
        <FormattedMessage id="detail_attacks_unknown_source">
          {(unknownText) => {
            return (
              <div
                className={cx([
                  {
                    [css`
                      color: ${theme.color.red50};
                    `]: status === AttackStatus.EXPLOITED
                  },
                  {
                    [css`
                      color: ${theme.color.yellow50};
                    `]: status === AttackStatus.SUSPICIOUS
                  },
                  {
                    [css`
                      color: ${theme.color.neutral60};
                    `]: status === AttackStatus.PROBED
                  }
                ])}
              >
                <IPTooltip sourceIPs={sourceIPs} sourceName={sourceName}>
                  {({ ref, onMouseEnter, onMouseLeave }) => {
                    return (
                      <span
                        data-e2e="attack-ip-address"
                        ref={ref}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                      >
                        {unknown ? unknownText : name}
                      </span>
                    )
                  }}
                </IPTooltip>
              </div>
            )
          }}
        </FormattedMessage>
        <AttackTypeLabels attackTypes={attackTypes} />
      </div>
      <div
        data-e2e="attack-count"
        className={cx([
          css`
            margin-left: 0px;
            padding-right: 15px;
            font-size: 24px;
            flex-shrink: 0;
          `,
          {
            [css`
              color: ${theme.color.red50};
            `]: status === AttackStatus.EXPLOITED
          },
          {
            [css`
              color: ${theme.color.yellow50};
            `]: status === AttackStatus.SUSPICIOUS
          }
        ])}
      >
        {totalAttacks}
      </div>
    </AttackerContainer>
  )
}

function AttackTypeLabels({
  attackTypes
}: {
  readonly attackTypes: AttackType[]
}) {
  return (
    <FormattedMessage id="detail_attacks_manualAttack">
      {(manualAttackText) => {
        return (
          <FormattedMessage id="detail_attacks_automatedScan">
            {(automatedAttackText) => {
              const isAutomated = attackTypes.indexOf(AttackType.AUTOMATED) > -1
              const isManual = attackTypes.indexOf(AttackType.MANUAL) > -1
              return (
                <div className={metadataCss} data-e2e="attack-type-label">
                  {isManual && isAutomated
                    ? `${automatedAttackText}, ${manualAttackText}`
                    : isManual
                    ? manualAttackText
                    : isAutomated
                    ? automatedAttackText
                    : ''}
                </div>
              )
            }}
          </FormattedMessage>
        )
      }}
    </FormattedMessage>
  )
}

function IPTooltip({
  children,
  sourceIPs,
  sourceName
}: {
  readonly children: (tooltipProps: {
    readonly ref?: PopperRefHandler
    readonly onMouseEnter?: () => void
    readonly onMouseLeave?: () => void
  }) => React.ReactNode
  readonly sourceIPs: string[]
  readonly sourceName?: string
}) {
  return sourceIPs.length > 0 && !!sourceName ? (
    <WithTooltip
      renderTooltipContent={() => {
        return (
          <div
            className={css`
              width: 100px;
            `}
          >
            <FormattedMessage
              id={
                sourceIPs.length > 1 ? 'title_ipAddresses' : 'title_ipAddress'
              }
            >
              {(ipAddressesText) => {
                return <div className={tooltipTitleCss}>{ipAddressesText}:</div>
              }}
            </FormattedMessage>
            {sourceIPs.map((sourceIP, index) => {
              return <div key={index}>{sourceIP}</div>
            })}
          </div>
        )
      }}
    >
      {children}
    </WithTooltip>
  ) : (
    <React.Fragment>{children({})}</React.Fragment>
  )
}

export function RemainingAttackers({
  attackerCount,
  attackCount,
  disabled,
  onClick
}: RemainingAttackers) {
  return (
    <AttackerContainer
      disabled={disabled}
      onClick={(e) => onClick({ shiftKey: e.shiftKey })}
      last={true}
    >
      <IconContainer>{`+ ${attackerCount}`}</IconContainer>
      <FormattedMessage
        id="plural_attacksMonitor_otherAttackers"
        values={{ count: attackerCount }}
      >
        {(otherAttackersText) => {
          return (
            <div
              className={css`
                padding-left: 8px;
                flex-grow: 1;
              `}
            >
              {otherAttackersText}
            </div>
          )
        }}
      </FormattedMessage>
      <div
        className={css`
          margin-left: 0px;
          padding-right: 15px;
          flex-shrink: 0;
          color: ${theme.color.neutral60};
        `}
      >
        {attackCount}
      </div>
    </AttackerContainer>
  )
}

function AttackerContainer({
  children,
  disabled,
  last,
  onClick,
  onMouseEnter,
  onMouseLeave
}: {
  readonly children: React.ReactNode
  readonly disabled: boolean
  readonly last: boolean
  readonly onClick: React.MouseEventHandler<Element>
  readonly onMouseEnter?: React.MouseEventHandler<Element>
  readonly onMouseLeave?: React.MouseEventHandler<Element>
}) {
  return (
    <div
      data-e2e="attacker-container"
      className={cx([
        css`
          align-items: center;
          display: flex;
          box-sizing: border-box;
          transition: background-color 0.25s, opacity 0.25s,
            max-height ${transitionDuration}ms;
          height: 70px;
          cursor: pointer;
          user-select: none;

          &:hover {
            background-color: rgba(128, 128, 128, 0.1);
          }

          &.${transitionName}-enter, &.${transitionName}-exit {
            transition: all linear ${transitionDuration}ms;
          }

          &.${transitionName}-enter,
            &.${transitionName}-exit.${transitionName}-exit-active {
            opacity: 0;
            max-height: 0;
          }

          &.${transitionName}-exit,
            &.${transitionName}-enter.${transitionName}-enter-active {
            opacity: 1;
            max-height: 70px;
          }
        `,
        {
          [css`
            cursor: default;
            opacity: 0.3;

            &:hover {
              opacity: 1;
            }
          `]: disabled
        },
        {
          [css`
            border-bottom: 1px solid ${theme.color.neutral30};
          `]: !last
        }
      ])}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  )
}

function UserIcon({ known }: { readonly known: boolean }) {
  return (
    <IconContainer>
      <CsUserIcon size="36" />
      {!known && (
        <span
          className={css`
            color: ${theme.color.white};
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-40%, -72%);
            font-weight: ${deprecatedTheme.fontWeights.primary.bold};
          `}
        >
          ?
        </span>
      )}
    </IconContainer>
  )
}

function IconContainer({ children }) {
  return (
    <div
      className={css`
        text-align: center;
        position: relative;
        width: 55px;
        color: ${theme.color.neutral70};
      `}
    >
      {children}
    </div>
  )
}
